import { readJsonConfig } from "./helpers/config_helpers";
import {
	loadProductSection,
	shopifyFiltersToAlgoliaFilters,
	DIGITAL_INDEX_ATTRIBUTE_OVERRIDES,
	batchDelay,
} from "./helpers/shopify_helper";
import { AlgoliaClient, SearchResponse } from "./algolia";
import { onQueryChange } from "./helpers/querystring_watcher";

declare global {
	interface Window {
		lastAlgoliaResponse?: any;
	}
}

console.log("Algolia search-page script");

interface SearchPageI18n {
	resultsCountZero: string;
	resultsCountOne: string;
	resultsCountOther: string;
	/** Quando le card renderizzate sono meno dei record Algolia (paginazione / errori section). */
	resultsCountOtherPartial?: string;
}

interface SearchPageConfig {
	apiKey: string;
	appId: string;
	index: string;
	containerId: string;
	sectionId: string;
	moreText: string;
	size: number;
	/** Stesso schema della PLP: mappa sort_by Shopify → nome indice/replica Algolia */
	sortIndices?: Record<string, string>;
	/** Segnaposto: `__COUNT__`, `__QUERY__`, opz. `__VISIBLE__` + `__TOTAL__` (vedi `search.results_count_*` nei locales) */
	i18n?: SearchPageI18n;
}

type ProductHit = { handle?: string; objectID: string };

function chunk<T>(arr: T[], size: number): T[][] {
	if (size <= 0) throw new Error("chunk size must be > 0");
	const out: T[][] = [];
	for (let i = 0; i < arr.length; i += size) {
		out.push(arr.slice(i, i + size));
	}
	return out;
}

function getQueryFromUrl(): string {
	return new URLSearchParams(location.search).get("q")?.trim() ?? "";
}

function init() {
	const config = readJsonConfig<SearchPageConfig>("algolia-search");
	if (!config) {
		console.warn("[AlgoliaSearchPage] config #algolia-search non trovata");
		return;
	}

	let first = true;
	let loading = false;
	let lastResponse: SearchResponse<ProductHit> | null = null;

	const client = new AlgoliaClient(config.apiKey, config.appId, config.index);

	/**
	 * Allinea l'indice Algolia a `sort_by` nell'URL (come su main.ts / PLP).
	 * Senza questo, la ricerca testuale usa sempre l'indice base e l'ordine non cambia mai.
	 */
	function resolveAndSetIndex(): void {
		if (!config.sortIndices) return;
		const params = new URLSearchParams(location.search);
		const sortBy = params.get("sort_by") || "manual";
		const targetIndex = config.sortIndices[sortBy] || config.index || "";
		if (targetIndex && targetIndex !== client.getIndex()) {
			console.log("[AlgoliaSearchPage] switching index for sort:", {
				sortBy,
				from: client.getIndex(),
				to: targetIndex,
			});
			client.setIndex(targetIndex);
		}
	}

	/**
	 * True se l'indice configurato è la custom integration (_digital).
	 */
	const isDigitalIndex = config.index.includes("_digital");

	const input = document.querySelector<HTMLInputElement>("[data-search-page-input]");
	const currentQuery = getQueryFromUrl();

	/** Evita che eventi `input` (es. iOS) durante l'impostazione programmatica della query svuotino l'URL. */
	let syncingInputFromScript = false;
	function setSearchInputValue(value: string) {
		if (!input) return;
		syncingInputFromScript = true;
		input.value = value;
		queueMicrotask(() => {
			syncingInputFromScript = false;
		});
	}
	if (input && currentQuery) {
		setSearchInputValue(currentQuery);
	}

	/** Le traduzioni passate da Liquid possono contenere entità HTML (es. &quot;) al posto delle virgolette. */
	function decodeHtmlEntities(s: string): string {
		if (typeof document === "undefined") return s;
		const t = document.createElement("textarea");
		t.innerHTML = s;
		return t.value;
	}

	function formatResultsCountMessage(
		template: string,
		nbHits: number,
		query: string
	): string {
		const tpl = decodeHtmlEntities(template);
		return tpl
			.replace(/__COUNT__/g, nbHits.toLocaleString())
			.replace(/__QUERY__/g, query);
	}

	function formatResultsPartialMessage(
		template: string,
		visible: number,
		total: number,
		query: string
	): string {
		const tpl = decodeHtmlEntities(template);
		return tpl
			.replace(/__VISIBLE__/g, visible.toLocaleString())
			.replace(/__TOTAL__/g, total.toLocaleString())
			.replace(/__QUERY__/g, query);
	}

	function updateResultsCount(nbHits: number, query: string, rendered?: number) {
		const el = document.querySelector("[data-search-results-count]");
		if (!el) return;
		if (!query) {
			el.classList.add("invisible");
			el.textContent = "";
			return;
		}
		const i18n = config.i18n;
		const partialTpl =
			i18n?.resultsCountOtherPartial?.trim() ||
			'__VISIBLE__ su __TOTAL__ risultati per "__QUERY__"';
		const showPartial =
			rendered !== undefined && nbHits > 0 && rendered !== nbHits;

		if (showPartial) {
			el.textContent = formatResultsPartialMessage(
				partialTpl,
				rendered,
				nbHits,
				query
			);
			el.classList.remove("invisible");
			return;
		}

		if (nbHits === 0) {
			const tpl =
				i18n?.resultsCountZero ??
				'Nessun risultato per "__QUERY__"';
			el.textContent = formatResultsCountMessage(tpl, nbHits, query);
		} else if (nbHits === 1) {
			const tpl =
				i18n?.resultsCountOne ?? '1 risultato per "__QUERY__"';
			el.textContent = formatResultsCountMessage(tpl, nbHits, query);
		} else {
			const tpl =
				i18n?.resultsCountOther ??
				'__COUNT__ risultati per "__QUERY__"';
			el.textContent = formatResultsCountMessage(tpl, nbHits, query);
		}
		el.classList.remove("invisible");
	}

	function buildFilters(search: string) {
		const params = new URLSearchParams(search);
		const digitalFilters = shopifyFiltersToAlgoliaFilters(params, {
			stripMetaPrefix: true,
			attributeOverrides: DIGITAL_INDEX_ATTRIBUTE_OVERRIDES,
		});
		const legacyFilters = shopifyFiltersToAlgoliaFilters(params);
		return { digitalFilters, legacyFilters };
	}

	function setLoadMoreVisible(visible: boolean) {
		const el = document.getElementById("search-load-more");
		if (!el) return;
		el.classList.toggle("d-none", !visible);
		el.classList.toggle("d-flex", visible);
	}

	async function performSearch(page: number, query: string, filters: string[] = [], legacyFilters: string[] = []) {
		if (loading) return;
		loading = true;

		// Nascondi il bottone durante il caricamento
		if (page === 0) setLoadMoreVisible(false);

		const container = document.getElementById(config!.containerId);
		if (!container) {
			console.warn("[AlgoliaSearchPage] container non trovato:", config!.containerId);
			loading = false;
			return;
		}

		if (!query.trim()) {
			container.innerHTML = `<div class="col-12 text-center py-5">
				${document.querySelector("#algolia-no-results-template")?.textContent ?? "Inserisci un termine di ricerca"}
			</div>`;
			updateResultsCount(0, "");
			loading = false;
			return;
		}

		const activeFilters = isDigitalIndex ? filters : legacyFilters;
		const allFilters = [...activeFilters, "status:ACTIVE"];
		resolveAndSetIndex();
		console.log("[AlgoliaSearchPage] search", {
			query,
			page,
			allFilters,
			isDigitalIndex,
			index: client.getIndex(),
		});
		const response = await client.rawSearch<ProductHit>(
			query, config!.size || 16, page, allFilters
		);
		console.log(`[AlgoliaSearchPage] ← ${client.getIndex()}`, {
			nbHits: response.nbHits,
			appliedRules: Array.isArray((response as any).appliedRules) && (response as any).appliedRules.length > 0
				? (response as any).appliedRules.map((r: any) => r.objectID ?? r)
				: "(none)",
		});

		lastResponse = response;
		window.lastAlgoliaResponse = response;

		if (response.hits.length === 0 && page === 0) {
			const emptyDetail = { ...response, renderedProductCount: 0 };
			window.dispatchEvent(new CustomEvent("algolia-search-page-complete", { detail: emptyDetail }));
			updateResultsCount(response.nbHits, query, 0);
			const template = document.querySelector("#algolia-no-results-template") as HTMLTemplateElement;
			container.innerHTML = "";
			if (template) {
				container.appendChild(template.content.cloneNode(true));
			} else {
				container.innerHTML = `<div class="col-12 text-center py-5">Nessun prodotto trovato</div>`;
			}
			setLoadMoreVisible(false);
			loading = false;
			return;
		}

		// Eventi Algolia Insights: ogni card porta queryID e posizione della risposta che l'ha generata
		const hitQueryID = (response as any).queryID as string | undefined;
		const hitPositionBase = (response.page ?? 0) * ((response as any).hitsPerPage ?? (config!.size || 16));
		const hitPositions = new Map<string, number>(
			response.hits.map((hit, i) => [String(hit.objectID), hitPositionBase + i + 1])
		);

		const BATCH_SIZE = 2;
		const INTER_BATCH_DELAY = 150;
		const chunks = chunk(response.hits, BATCH_SIZE);

		for (let ci = 0; ci < chunks.length; ci++) {
			const c = chunks[ci];

			if (ci > 0) await batchDelay(INTER_BATCH_DELAY);

			const missingHandleHits = c.filter(
				(hit) => typeof hit?.handle !== "string" || hit.handle.trim().length === 0
			);
			if (missingHandleHits.length > 0) {
				console.warn("[AlgoliaSearchPage] hits missing handle", missingHandleHits.map((h) => h.objectID));
			}

			const hitsWithHandle = c
				.map((hit) => ({
					...hit,
					handle: typeof hit.handle === "string" ? hit.handle.trim() : "",
				}))
				.filter((hit) => hit.handle.length > 0);

			const productSections = await Promise.allSettled(
				hitsWithHandle.map(async (hit) => {
					try {
						const section = await loadProductSection(config!.sectionId, hit.handle);
						return { hit, section };
					} catch (error) {
						const message = error instanceof Error ? error.message : String(error);
						throw new Error(`Search page section load failed for ${hit.objectID} (${hit.handle}): ${message}`);
					}
				})
			);

			const frag = document.createDocumentFragment();

			for (const result of productSections) {
				if (result.status === "rejected") {
					console.warn("[AlgoliaSearchPage] product section load failed", result.reason);
					continue;
				}
				const { hit, section } = result.value;
				const doc = new DOMParser().parseFromString(section, "text/html");
				const el = doc.querySelector(".shopify-section");
				if (!el) {
					console.warn("[AlgoliaSearchPage] product section missing .shopify-section", {
						objectID: hit.objectID,
						handle: hit.handle,
					});
					continue;
				}
				const productCard = el.querySelector("product-card");
				if (!productCard || !productCard.getAttribute("data-product-id"))
					continue;

				el.removeAttribute("id");
				if (hitQueryID) el.setAttribute("data-algolia-query-id", hitQueryID);
				el.setAttribute("data-algolia-position", String(hitPositions.get(String(hit.objectID)) ?? ""));
				frag.appendChild(el);
			}

			if (first) container.innerHTML = "";
			container.appendChild(frag);
			first = false;
		}

		const hasMorePages = (response.page ?? 0) + 1 < (response.nbPages ?? 0);
		setLoadMoreVisible(hasMorePages);

		const renderedProductCount = container.querySelectorAll("product-card[data-product-id]").length;
		window.dispatchEvent(
			new CustomEvent("algolia-search-page-complete", {
				detail: { ...response, renderedProductCount },
			})
		);
		updateResultsCount(response.nbHits, query, renderedProductCount);

		loading = false;
	}

	let currentFilters = buildFilters(location.search);
	let currentSearchQuery = getQueryFromUrl();

	// Registra subito (non dopo la prima ricerca): così sort/filtri che aggiornano l'URL
	// mentre il primo fetch è in corso non vanno persi.
	onQueryChange(({ newSearch }) => {
		currentFilters = buildFilters(newSearch);
		currentSearchQuery = new URLSearchParams(newSearch).get("q")?.trim() ?? "";
		first = true;
		if (input && new URLSearchParams(newSearch).has("q")) {
			setSearchInputValue(currentSearchQuery);
		}
		performSearch(0, currentSearchQuery, currentFilters.digitalFilters, currentFilters.legacyFilters);
	});

	// Load more
	window.addEventListener("algolia-search-load-more", () => {
		if (!lastResponse) return;
		const nextPage = (lastResponse.page ?? 0) + 1;
		if (nextPage >= (lastResponse.nbPages ?? 0)) return;
		performSearch(
			nextPage,
			currentSearchQuery,
			currentFilters.digitalFilters,
			currentFilters.legacyFilters
		);
	});

	function pushQueryToHistory(q: string) {
		const url = new URL(location.href);
		if (q) url.searchParams.set("q", q);
		else url.searchParams.delete("q");
		const nextHref = url.pathname + url.search + url.hash;
		if (nextHref === location.pathname + location.search + location.hash) return;
		history.pushState({}, "", nextHref);
	}

	// Non c'è più <form>: Invio sulla tastiera aggiorna l'URL come prima al submit
	input?.addEventListener("keydown", (e) => {
		if (e.key !== "Enter") return;
		e.preventDefault();
		pushQueryToHistory(input.value.trim());
	});

	// Input con debounce (400ms)
	let debounceTimer: ReturnType<typeof setTimeout>;
	input?.addEventListener("input", () => {
		if (syncingInputFromScript) return;
		clearTimeout(debounceTimer);
		debounceTimer = setTimeout(() => {
			if (syncingInputFromScript || !input) return;
			const q = input.value.trim();
			const url = new URL(location.href);
			if (q) {
				url.searchParams.set("q", q);
			} else {
				url.searchParams.delete("q");
			}
			const nextHref = url.pathname + url.search + url.hash;
			if (nextHref === location.pathname + location.search + location.hash) return;
			history.pushState({}, "", nextHref);
		}, 400);
	});

	// Prima ricerca (URL già osservato da onQueryChange sopra)
	performSearch(0, currentSearchQuery, currentFilters.digitalFilters, currentFilters.legacyFilters);
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => init(), { once: true });
} else {
	init();
}
